import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ClassroomContext = createContext(null);

const STORAGE_KEY = 'activeClassroomId';

export function ClassroomProvider({ children }) {
  const { user } = useAuth();
  const [classrooms, setClassrooms] = useState([]);
  const [activeClassroom, setActiveClassroom] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchClassrooms = useCallback(async () => {
    if (!user) {
      setClassrooms([]);
      setActiveClassroom(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/teacher/classrooms`);
      setClassrooms(data);

      // Restore from localStorage or default to sandbox
      const savedId = localStorage.getItem(STORAGE_KEY);
      const saved = savedId && data.find(c => c.id === savedId);
      if (saved) {
        setActiveClassroom(saved);
      } else {
        // Default to sandbox
        const sandbox = data.find(c => c.type === 'sandbox');
        if (sandbox) {
          setActiveClassroom(sandbox);
          localStorage.setItem(STORAGE_KEY, sandbox.id);
        }
      }
    } catch {
      // ignore
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]);

  const switchClassroom = useCallback((classroom) => {
    setActiveClassroom(classroom);
    localStorage.setItem(STORAGE_KEY, classroom.id);
  }, []);

  const createClassroom = useCallback(async (name) => {
    const { data } = await axios.post(`${API_URL}/teacher/classrooms`, { name });
    setClassrooms(prev => [...prev, data]);
    return data;
  }, []);

  const renameClassroom = useCallback(async (id, name) => {
    await axios.put(`${API_URL}/teacher/classrooms/${id}`, { name });
    setClassrooms(prev => prev.map(c => c.id === id ? { ...c, name } : c));
    setActiveClassroom(prev => prev && prev.id === id ? { ...prev, name } : prev);
  }, []);

  const deleteClassroom = useCallback(async (id) => {
    await axios.delete(`${API_URL}/teacher/classrooms/${id}`);
    setClassrooms(prev => {
      const remaining = prev.filter(c => c.id !== id);
      // If deleted the active one, switch to sandbox
      if (activeClassroom?.id === id) {
        const sandbox = remaining.find(c => c.type === 'sandbox');
        if (sandbox) {
          setActiveClassroom(sandbox);
          localStorage.setItem(STORAGE_KEY, sandbox.id);
        }
      }
      return remaining;
    });
  }, [activeClassroom]);

  return (
    <ClassroomContext.Provider value={{
      classrooms,
      activeClassroom,
      loading,
      switchClassroom,
      createClassroom,
      renameClassroom,
      deleteClassroom,
      refreshClassrooms: fetchClassrooms,
    }}>
      {children}
    </ClassroomContext.Provider>
  );
}

export function useClassroom() {
  const ctx = useContext(ClassroomContext);
  if (!ctx) throw new Error('useClassroom must be used within ClassroomProvider');
  return ctx;
}
